import React, { useState, useRef } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import PayrollEdit from "./PayrollEdit";
import { useTranslation } from "react-i18next";
import { Months } from "../../../../utils";
import Print from "./Print";

const PayrollList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [filters, setFilters] = useState({
    month: "",
    year: "",
    employee: "",
  });

  const { handleDelete, ConfirmDialog } = useDelete("employee-payroll", token);

  // Fetch employees
  const {
    data: employees,
    isLoading: isEmployeesLoading,
    isError: isEmployeesError,
    error: employeesError,
  } = useFetchObjects(["employees"], "users/", token);

  // Build endpoint for payroll data
  const endpoint = `employee-payroll/?month=${filters.month}&year=${filters.year}&employee=${filters.employee}`;
  const { data, isLoading, isError, error } = useFetchObjects(
    ["employee-payroll", filters],
    endpoint,
    token
  );

  const [open, setOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const handleOpen = (item) => {
    setSelectedPayroll(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPayroll(null);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handlePrintClick = (item) => {
    setPrintData(item);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  // If employees or payroll data are loading or erroring out:
  if (isEmployeesLoading) {
    return <Typography>{t("hr.loading")} (Employees)...</Typography>;
  }
  if (isEmployeesError) {
    return <Typography>Error: {employeesError.message}</Typography>;
  }
  if (isLoading) {
    return <Typography>{t("hr.loading")} (Payroll)...</Typography>;
  }
  if (isError) {
    return <Typography>Error: {error.message}</Typography>;
  }

  // Function to calculate remaining salary for each payroll item:
  // const calculateRemaining = (item) => {
  //   const totalSalary = parseFloat(item.total_salary) || 0;
  //   const bonus = parseFloat(item.total_bonus) || 0;
  //   const paid = parseFloat(item.paid_salary) || 0;
  //   const advance = parseFloat(item.total_advance) || 0;
  //   const retention = parseFloat(item.retention_amount) || 0;
  //   return (totalSalary + bonus) - (paid + advance + retention);
  // };
  const calculateRemaining = (item) => {
    const totalSalary = parseFloat(item.total_salary) || 0;
    const bonus = parseFloat(item.total_bonus) || 0;
    const paid = parseFloat(item.paid_salary) || 0;
    const advance = parseFloat(item.total_advance) || 0;
    const retention = parseFloat(item.retention_amount) || 0;

    // Calculate the remaining salary
    let remaining = totalSalary + bonus - (paid + advance + retention);

    // If remaining salary is less than 0, set it to 0
    if (remaining < 0) {
      remaining = 0;
    }

    return remaining;
  };

  return (
    <Grid container padding="10px">
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <TextField
            select
            label={t("hr.month")}
            name="month"
            value={filters.month}
            onChange={handleFilterChange}
            fullWidth
          >
            {Months.map((item, index) => (
              <MenuItem key={index} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField
            select
            label={t("hr.year")}
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            fullWidth
          >
            {Array.from(
              { length: new Date().getFullYear() - 2020 + 1 },
              (_, index) => (
                <MenuItem key={2020 + index} value={2020 + index}>
                  {2020 + index}
                </MenuItem>
              )
            )}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField
            select
            label={t("hr.employee")}
            name="employee"
            value={filters.employee}
            onChange={handleFilterChange}
            fullWidth
          >
            {(employees || []).map((emp, index) => (
              <MenuItem key={index} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <TableContainer sx={{ mt: 2 }}>
        <Table sx={{ minWidth: "100%" }} aria-label="payroll table">
          <TableHead style={{ backgroundColor: "#f2f2f2" }}>
            <TableRow>
              <TableCell align="center">{t("hr.employee")}</TableCell>
              <TableCell align="center">{t("hr.month")}</TableCell>
              <TableCell align="center">{t("hr.year")}</TableCell>
              <TableCell align="center">{t("hr.totalSalary")}</TableCell>
              <TableCell align="center">{t("hr.advanceAmount")}</TableCell>
              <TableCell align="center">{t("hr.bonusAmount")}</TableCell>
              <TableCell align="center">{t("hr.paidSalary")}</TableCell>
              <TableCell align="center">{t("hr.retentionAmount")}</TableCell>
              <TableCell align="center">{t("hr.remainingSalary")}</TableCell>
              <TableCell align="center">{t("hr.update")}</TableCell>
              <TableCell align="center">{t("hr.delete")}</TableCell>
              <TableCell align="center">{t("hr.print")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || []).map((item, index) => (
              <TableRow key={index}>
                <TableCell align="center">{item.employee_display}</TableCell>
                <TableCell align="center">{item.month_display}</TableCell>
                <TableCell align="center">{item.year}</TableCell>
                <TableCell align="center">{item.total_salary}</TableCell>
                <TableCell align="center">{item.total_advance}</TableCell>
                <TableCell align="center">{item.total_bonus}</TableCell>
                <TableCell align="center">{item.paid_salary}</TableCell>
                <TableCell align="center">{item.retention_amount}</TableCell>
                <TableCell align="center">{calculateRemaining(item)}</TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      boxShadow: "0",
                      textTransform: "capitalize",
                      color: "green",
                      backgroundColor: "lightgreen",
                      "&:hover": {
                        backgroundColor: "green",
                        color: "white",
                      },
                    }}
                    onClick={() => handleOpen(item)}
                  >
                    {t("hr.update")}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <Button
                    onClick={() => handleDelete(item.id)}
                    size="small"
                    variant="contained"
                    sx={{
                      boxShadow: "0",
                      textTransform: "capitalize",
                      color: "red",
                      backgroundColor: "pink",
                      "&:hover": {
                        backgroundColor: "red",
                        color: "white",
                      },
                    }}
                  >
                    {t("hr.delete")}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handlePrintClick(item)}
                  >
                    <PrintIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm delete dialog */}
      <ConfirmDialog />

      {/* Edit Payroll Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("hr.editPayroll")}</DialogTitle>
        <DialogContent>
          <PayrollEdit
            payroll={selectedPayroll}
            onClose={handleClose}
            token={token}
          />
        </DialogContent>
      </Dialog>

      {/* Hidden Print Component */}
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <Print data={printData} />
        </div>
      )}
    </Grid>
  );
};

export default PayrollList;
